import { useEffect, useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { BreadcrumbSchema } from '@/components/feature/Breadcrumb';
import ScrollToTopButton from '@/components/base/ScrollToTop';
import { testimonials } from '@/mocks/articles';

function AboutSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于黄油游戏官方指南',
    description: '黄油游戏官方指南是国内领先的黄油游戏攻略资源平台，致力于为每一位黄油游戏玩家提供最全面、最专业的游戏攻略、玩法教程和资源大全。',
    url: 'https://butter-game-guide.readdy.ai/about',
  };
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
  );
}

export default function AboutPage() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 黄油游戏官方指南';
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9qui4snlsngrm4lm6cg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (typeof serverMsg === 'string' && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <AboutSchema />
      <Navbar />
      <ScrollToTopButton />

      <main className="pt-20">
        <section className="px-4 md:px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: t('breadcrumb.home'), path: '/' },
                { label: t('about.title') },
              ]}
            />
            <BreadcrumbSchema
              items={[
                { label: t('breadcrumb.home'), path: '/' },
                { label: t('about.title') },
              ]}
            />

            <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 font-heading mb-2">
              {t('about.title')}
            </h1>
            <p className="text-foreground-600 mb-10">{t('about.subtitle')}</p>

            {/* Brand Introduction */}
            <section className="mb-14">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-building-2-line text-lg"></i>
                </div>
                <h2 className="text-xl font-semibold text-foreground-900 font-heading">
                  {t('about.intro')}
                </h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <p className="text-foreground-700 leading-relaxed mb-4">
                    {t('about.intro.content')}
                  </p>
                  <p className="text-foreground-700 leading-relaxed">
                    我们的团队由一群热爱黄油游戏的资深玩家和专业内容创作者组成。每一篇攻略都经过反复验证和精心打磨，确保信息的准确性和实用性。我们不仅关注游戏的表层玩法，更深入挖掘隐藏内容、角色故事和进阶技巧，力求为玩家呈现最完整的黄油游戏攻略内容。
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-5 rounded-xl bg-background-100 text-center">
                    <p className="text-3xl font-bold text-primary-500 font-heading mb-1">100万+</p>
                    <p className="text-sm text-foreground-600">服务玩家</p>
                  </div>
                  <div className="p-5 rounded-xl bg-background-100 text-center">
                    <p className="text-3xl font-bold text-accent-500 font-heading mb-1">200+</p>
                    <p className="text-sm text-foreground-600">精选攻略</p>
                  </div>
                  <div className="p-5 rounded-xl bg-background-100 text-center">
                    <p className="text-3xl font-bold text-secondary-500 font-heading mb-1">99%</p>
                    <p className="text-sm text-foreground-600">好评率</p>
                  </div>
                  <div className="p-5 rounded-xl bg-background-100 text-center">
                    <p className="text-3xl font-bold text-primary-500 font-heading mb-1">每日更新</p>
                    <p className="text-sm text-foreground-600">内容维护</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Reputation */}
            <section className="mb-14">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                  <i className="ri-heart-line text-lg"></i>
                </div>
                <h2 className="text-xl font-semibold text-foreground-900 font-heading">
                  {t('about.reputation')}
                </h2>
              </div>
              <p className="text-foreground-700 leading-relaxed mb-6">
                {t('about.reputation.content')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {testimonials.map((item, index) => (
                  <div
                    key={index}
                    className="p-5 rounded-xl bg-background-50 border border-background-200/70"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: item.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-400 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      &ldquo;{item.content}&rdquo;
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 font-semibold text-sm">
                        {item.name.charAt(item.name.length - 1)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">{item.name}</p>
                        <p className="text-xs text-foreground-500">{item.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact */}
            <section id="contact" className="mb-14">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                  <i className="ri-mail-send-line text-lg"></i>
                </div>
                <h2 className="text-xl font-semibold text-foreground-900 font-heading">
                  {t('about.contact')}
                </h2>
              </div>
              <p className="text-foreground-600 mb-8">{t('about.contact.desc')}</p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Contact Info */}
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">{t('about.email')}</p>
                      <p className="text-sm font-medium text-foreground-800">buttergame@example.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-wechat-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">{t('about.wechat')}</p>
                      <p className="text-sm font-medium text-foreground-800">ButterGame_Official</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-qq-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">{t('about.qq')}</p>
                      <p className="text-sm font-medium text-foreground-800">123456789</p>
                    </div>
                  </div>
                </div>

                {/* Contact Form */}
                <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <h3 className="text-lg font-semibold text-foreground-900 mb-4">
                    {t('contact.form.title')}
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
                    <input
                      type="text"
                      name="phone_alt"
                      className="honeypot-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        {t('contact.form.name')}
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的称呼"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        {t('contact.form.email')}
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入电子邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        {t('contact.form.message')}
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容（500字以内）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full py-2.5 rounded-lg bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                    >
                      {formStatus === 'submitting' ? '提交中...' : t('contact.form.submit')}
                    </button>

                    {formStatus === 'success' && (
                      <div className="p-3 rounded-lg bg-secondary-100 text-secondary-700 text-sm text-center">
                        <i className="ri-checkbox-circle-line mr-1"></i>
                        {t('contact.form.success')}
                      </div>
                    )}
                    {formStatus === 'error' && (
                      <div className="p-3 rounded-lg bg-accent-100 text-accent-700 text-sm text-center">
                        <i className="ri-error-warning-line mr-1"></i>
                        {formError}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}